import { Show } from "@refinedev/mui";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import React from "react";

export const DiscountShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow();
  const { data, isLoading } = queryResult;

  const record = data?.data;

  return (
    <Show isLoading={isLoading}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">
                      {record?.name || 'Unnamed Discount'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {record?.description || 'No description available'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={record?.active ? "Active" : "Inactive"}
                      color={record?.active ? "success" : "default"}
                      size="small"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Discount Details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Discount Details
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Type
                    </Typography>
                    <Chip
                      label={record?.type === 'percentage' ? 'Percentage Discount' : 'Fixed Amount Discount'}
                      color={record?.type === 'percentage' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Value
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {record?.type === 'percentage' ? (
                        `${record?.percentage}%`
                      ) : (
                        `${(record?.amount / 100).toFixed(2)} ${record?.currency?.toUpperCase()}`
                      )}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Usage Statistics */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Usage Statistics
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Times Used
                    </Typography>
                    <Typography variant="h5">
                      {record?.timesRedeemed || 0}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Maximum Uses
                    </Typography>
                    <Typography variant="body1">
                      {record?.maxRedemptions || 'Unlimited'}
                    </Typography>
                  </Box>

                  {record?.maxRedemptions && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Usage Rate
                      </Typography>
                      <Typography variant="body1">
                        {((record?.timesRedeemed || 0) / record?.maxRedemptions * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Expiration & Limits */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Expiration & Limits
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Expiration Date
                    </Typography>
                    {record?.redeemBy ? (
                      <Box>
                        <Typography variant="body1">
                          {new Date(record.redeemBy).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                        <Chip
                          label={new Date(record.redeemBy) < new Date() ? 'Expired' : 'Active'}
                          color={new Date(record.redeemBy) < new Date() ? 'error' : 'success'}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body1" color="success.main">
                        Never expires
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Product Restrictions */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Product Restrictions
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {record?.appliesTo && record.appliesTo.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      This discount applies only to the following products:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {record.appliesTo.map((productId: string, index: number) => (
                        <Chip
                          key={index}
                          label={productId}
                          variant="outlined"
                          color="primary"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" color="success.main">
                      ✅ Applies to all products
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No product restrictions - this discount can be used on any product.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Technical Details */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Technical Details
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Stripe ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {record?.id}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {record?.created ? new Date(record.created * 1000).toLocaleDateString() : 'Unknown'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body2">
                      {record?.type || 'Unknown'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Currency
                    </Typography>
                    <Typography variant="body2">
                      {record?.currency?.toUpperCase() || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Show>
  );
};
